/**
 * Responsibility: Implement config behavior for the ai layer.
 * Scope: Owns ai-layer concerns only cross-layer orchestration is excluded.
 */
// ============================================================
// AI/CONFIG.TS — Provider configuration + initialization
// Reads config from localStorage, creates providers, registers
// them with the router singleton. Import this module once as a
// side effect to ensure providers are registered before use.
// ============================================================

import { router } from './router'
import { getAppImportMetaEnv } from "@anyquest/shared-core"

export interface AIProviderConfig {
  geminiApiKey: string
  geminiProxyUrl: string
  geminiEnabled: boolean
  ollamaBaseUrl: string
  ollamaEnabled: boolean
  ollamaModel: string        // shared fallback / legacy
  ollamaFastModel: string    // logic_forge, world_tick, trait_shift ('' = ollamaModel)
  ollamaQualityModel: string // narrator, genesis, world_enrich      ('' = ollamaModel)
  ollamaHardware: string     // 'cpu' | '4gb' | '8gb' | '16gb' — used for model sorting hints
  openaiApiKey: string
  openaiEnabled: boolean
  anthropicApiKey: string
  anthropicEnabled: boolean
  groqApiKey: string
  groqEnabled: boolean
  stableDiffusionBaseUrl: string
  stableDiffusionEnabled: boolean
}

const STORAGE_KEY = 'am2_ai_config'
const META_ENV = getAppImportMetaEnv()
const E2E_MOCK_MODE = !META_ENV.PROD && META_ENV.VITE_E2E_MOCK_MODE === '1'

const DEFAULTS: AIProviderConfig = {
  geminiApiKey: String(META_ENV.VITE_GEMINI_API_KEY ?? ''),
  geminiProxyUrl: String(META_ENV.VITE_GEMINI_PROXY_URL ?? ''),
  geminiEnabled: false,
  // Always use the relative proxy path to allow the server/proxy to handle host resolution
  ollamaBaseUrl: '/api/ollama',
  ollamaEnabled: true,
  ollamaModel: 'llama3.2',
  ollamaFastModel: '',
  ollamaQualityModel: '',
  ollamaHardware: 'cpu',
  openaiApiKey: '',
  openaiEnabled: false,
  anthropicApiKey: '',
  anthropicEnabled: false,
  groqApiKey: '',
  groqEnabled: false,
  stableDiffusionBaseUrl: '/api/sd',
  stableDiffusionEnabled: false,
}
let ollamaPrewarmStarted = false

export function loadAIConfig(): AIProviderConfig {
  let cfg = { ...DEFAULTS }
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      cfg = { ...DEFAULTS, ...parsed }
    }
  } catch { /* ignore */ }

  // Automatically disable providers that lack keys
  if (cfg.openaiEnabled && !cfg.openaiApiKey) cfg.openaiEnabled = false
  if (cfg.anthropicEnabled && !cfg.anthropicApiKey) cfg.anthropicEnabled = false
  if (cfg.groqEnabled && !cfg.groqApiKey) cfg.groqEnabled = false
  // Gemini might use proxy URL as a key source if configured differently, but usually needs a key
  if (cfg.geminiEnabled && !cfg.geminiApiKey && !cfg.geminiProxyUrl) cfg.geminiEnabled = false

  // Prevent mixed-content errors in production by forcibly disabling
  // local HTTP Ollama if the app is served over HTTPS.
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && cfg.ollamaBaseUrl.startsWith('http://')) {
    cfg.ollamaEnabled = false
  }
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && cfg.stableDiffusionBaseUrl.startsWith('http://')) {
    cfg.stableDiffusionEnabled = false
  }

  return cfg
}

export function saveAIConfig(cfg: AIProviderConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg))
  applyAIConfig(cfg)
}

export async function applyAIConfig(cfg: AIProviderConfig): Promise<void> {
  // Clear existing providers and re-register based on current config
  router.clearProviders()

  if (E2E_MOCK_MODE) {
    const { MockProvider } = await import('./providers/mock')
    router.register(new MockProvider())
    return
  }
  
  // Register Managed AI (Vertex)
  const { VertexAIProvider } = await import('./providers/vertexai')
  router.register(new VertexAIProvider())

  // Gemini registered if explicitly enabled
  if (cfg.geminiEnabled === true) {
    const { GeminiProvider } = await import('./providers/gemini')
    router.register(new GeminiProvider(cfg.geminiApiKey, cfg.geminiProxyUrl))
  }

  if (cfg.openaiApiKey && cfg.openaiEnabled === true) {
    const { OpenAIProvider } = await import('./providers/openai')
    router.register(new OpenAIProvider(cfg.openaiApiKey))
  }
  if (cfg.anthropicApiKey && cfg.anthropicEnabled === true) {
    const { AnthropicProvider } = await import('./providers/anthropic')
    router.register(new AnthropicProvider(cfg.anthropicApiKey))
  }
  if (cfg.groqApiKey && cfg.groqEnabled === true) {
    const { GroqProvider } = await import('./providers/groq')
    router.register(new GroqProvider(cfg.groqApiKey))
  }
  
  // Register Image Providers
  const { ImagenProvider } = await import('./providers/imagen')
  router.register(new ImagenProvider(cfg.geminiApiKey, cfg.geminiProxyUrl))

  if (cfg.stableDiffusionEnabled) {
    const { StableDiffusionProvider } = await import('./providers/stable-diffusion')
    router.register(new StableDiffusionProvider(cfg.stableDiffusionBaseUrl))
  }

  if (cfg.ollamaEnabled) {
    const { OllamaProvider } = await import('./providers/ollama')
    const base = cfg.ollamaModel || 'llama3.2'
    const fast = cfg.ollamaFastModel || base
    const quality = cfg.ollamaQualityModel || base
    router.register(new OllamaProvider(cfg.ollamaBaseUrl, fast, quality, cfg.ollamaHardware))
  }

  // Final fallback: Always register Mock as the last resort to prevent crash
  const { MockProvider } = await import('./providers/mock')
  router.register(new MockProvider())
}

export async function prewarmLocalModels(cfg: AIProviderConfig = loadAIConfig()): Promise<void> {
  if (E2E_MOCK_MODE || !cfg.ollamaEnabled || ollamaPrewarmStarted) return
  ollamaPrewarmStarted = true
  try {
    const { OllamaProvider } = await import('./providers/ollama')
    const base = cfg.ollamaModel || 'llama3.2'
    const fast = cfg.ollamaFastModel || base
    const quality = cfg.ollamaQualityModel || base
    const provider = new OllamaProvider(cfg.ollamaBaseUrl, fast, quality, cfg.ollamaHardware)
    await provider.prewarm('logic_forge')
    if (quality !== fast) await provider.prewarm('narrator')
  } catch (error) {
    console.warn('[AI] Ollama prewarm skipped:', error)
  }
}

// Initialize on module load (side effect)
applyAIConfig(loadAIConfig())

/** Returns true if at least one CUSTOM AI provider (cloud key or local Ollama) is configured. */
export function hasAnyAIKey(cfg: AIProviderConfig = loadAIConfig()): boolean {
  if (E2E_MOCK_MODE) return true
  const has = (value: unknown) => String(value ?? '').trim().length > 0
  return !!(
    has(cfg.geminiApiKey)
    || has(cfg.geminiProxyUrl)
    || has(cfg.openaiApiKey)
    || has(cfg.anthropicApiKey)
    || has(cfg.groqApiKey)
    || cfg.ollamaEnabled
  )
}

// Re-export router for convenient import from a single place
export { router }
